import { Button, ButtonProps } from "@mui/material";
import styles from "./RadioCard.module.css";
import { ComponentPropsWithoutRef } from "react";
import CheckmarkIcon from "../images/checkmark.svg?react";

export type CoreRadioCardIconProps = {
	icon: React.ReactNode;
	enabled: boolean;
	/** @default 40px */
	width?: number;
	/**@default 40px */
	height?: number;
	/**@default false */
	showCheckmark?: boolean;
	className?: string;
};

/**Adds enabled and not enabled styling. */
export function CoreRadioCardIcon({
	icon,
	enabled,
	height,
	width,
	showCheckmark = false,
	className,
}: CoreRadioCardIconProps) {
	const finalWidth = `${width ?? 40}px`;
	const finalHeight = `${height ?? 40}px`;
	return (
		<div
			style={{ width: finalWidth, height: finalHeight }}
			className={`${styles.radioIconContainer} ${className || ""} ${enabled ? styles.enabled : ""}`}
		>
			{icon}
			{enabled && showCheckmark && (
				<div className={styles.greenCheck}>
					<CheckmarkIcon />
				</div>
			)}
		</div>
	);
}

type CoreRadioCardSelectIconProps = {
	selected: boolean;
	/**If true, will show empty circle when not selected. @default false */
	showUnselectedUI?: boolean;
	/**@default 24px */
	width?: string | number;
	/**@default 24px */
	height?: string | number;
} & Omit<React.HTMLAttributes<HTMLDivElement>, "children">;

/**Purple checkmark */
export function CoreRadioCardSelectIcon({
	selected,
	width = 24,
	height = 24,
	showUnselectedUI,
	...props
}: CoreRadioCardSelectIconProps) {
	const finalWidth = typeof width === "number" ? `${width}px` : width;
	const finalHeight = typeof height === "number" ? `${height}px` : height;

	const isVisible = selected || showUnselectedUI;

	const classNames = [styles.selectIconWrapper];
	if (selected) classNames.push(styles.selected);
	if (!selected) classNames.push(styles.notSelected);
	if (!isVisible) classNames.push(styles.hidden);
	if (props.className) classNames.push(props.className);

	const finalClassName = classNames.join(" ");

	return (
		<div
			{...props}
			className={finalClassName}
			style={{ ...props.style, width: finalWidth, height: finalHeight }}
			aria-hidden={true}
		>
			{selected && <CheckmarkIcon />}
		</div>
	);
}

type CoreRadioCardProps = {
	selected: boolean;
	onClick: () => void;
	disabled?: boolean;
	children: React.ReactNode;
	secondaryAction?: React.ReactNode;
	slotProps?: {
		container?: Omit<ComponentPropsWithoutRef<"div">, "children">;
		button?: Omit<ButtonProps, "onClick" | "children">;
		actionWrapper?: Omit<ComponentPropsWithoutRef<"div">, "children">;
	};
};

export default function CoreRadioCard({
	children,
	onClick,
	secondaryAction,
	selected,
	disabled,
	slotProps,
}: CoreRadioCardProps) {
	const containerClassNames = [styles.radioCardContainer];

	if (selected) containerClassNames.push(styles.selected);
	if (!selected) containerClassNames.push(styles.notSelected);
	if (disabled) containerClassNames.push(styles.disabled);
	if (slotProps?.container?.className) containerClassNames.push(slotProps.container.className);

	const containerClassName = containerClassNames.join(" ");

	// Action is out of button because accessibility says no clickables in clickables
	return (
		<div {...slotProps?.container} className={containerClassName}>
			<Button
				{...slotProps?.button}
				onClick={onClick}
				className={`${styles.radioCardButton}  ${slotProps?.button?.className || ""}`}
				aria-pressed={selected}
				disabled={disabled}
			>
				{children}
			</Button>
			{secondaryAction && (
				<div className={`${styles.secondaryActionWrapper} ${slotProps?.actionWrapper?.className || ""}`}>
					{secondaryAction}
				</div>
			)}
		</div>
	);
}
